// src/pages/ModelInsightsPage.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ModelInsightsPanel from '../components/ModelInsightsPanel';
import '../styles/ModelInsightsPage.css';
import '../styles/ModelInsightsPanel.css';
import { fetchModelInsights, getCachedModelInsights } from '../utils/api';
import { useAppContext } from '../context/AppContext';

const ModelInsightsPage = () => {
  const { user, token, fingerprint, modelInsightsData, hasUploaded, isDemo } = useAppContext();
  const navigate = useNavigate();

  // Gate ONLY if they’ve uploaded (non-demo) and are on trial/free.
  const trialLocked = !!(user?.trial_active && !user?.is_admin);
  const shouldGateToSubscription = trialLocked && hasUploaded && !isDemo;

  useEffect(() => {
    if (shouldGateToSubscription) {
      navigate('/subscription', { replace: true, state: { from: '/model-insights' } });
    }
  }, [shouldGateToSubscription, navigate]);

  if (shouldGateToSubscription) return null; // avoid flicker while redirecting

  // prefer context cache, else per-user+fingerprint cache
  const cachedInitial =
    modelInsightsData || (user ? getCachedModelInsights(user.id, fingerprint) : null);

  const [data, setData] = useState(cachedInitial);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(!cachedInitial);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!data) setLoading(true);
      try {
        const result = await fetchModelInsights({
          force: false,
          token,
          userId: user?.id || null,
          fingerprint
        });
        if (mounted) {
          setData(result || null);
          setErr(null);
        }
      } catch (e) {
        if (mounted) setErr(e?.message || 'Failed to load model insights');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, token, fingerprint]);

  if (loading) {
    return (
      <div className="model-insights-page force-light">
        <h2 className="section-title">Model Insights</h2>
        <div className="panel-skeleton">
          <div className="skeleton-row" />
          <div className="skeleton-row" />
          <div className="skeleton-grid">
            <div className="skeleton-card" />
            <div className="skeleton-card" />
            <div className="skeleton-card" />
            <div className="skeleton-card" />
          </div>
          <div className="skeleton-chart" />
          <div className="skeleton-chart" />
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="model-insights-page force-light">
        <h2 className="section-title">Model Insights</h2>
        <div className="error-box">
          <div className="error-title">Could not load insights</div>
          <div className="error-body">{err}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="model-insights-page force-light">
        <h2 className="section-title">Model Insights</h2>
        <div className="error-box">
          <div className="error-title">No data returned</div>
          <div className="error-body">The server returned an empty response. Try reloading or open a CSV first.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="model-insights-page force-light">
      <header className="mi-header">
        <h1 className="mi-title">Model Insights</h1>
        <p className="mi-subtitle">Evaluate the performance and features of your model.</p>
      </header>


      <ModelInsightsPanel
        /* overview */
        forecasts={data.forecasts || []}
        accuracy={data.accuracy || []}
        spikes={data.spikes || []}
        summary={data.summary || ''}

        /* time series */
        chartLabels={data.chart_labels || []}
        chartSeries={data.chart_series || {}}

        /* explainability & evaluation */
        backtests={data.backtests || {}}
        residualsSeries={data.residuals_series || {}}
        regMetricsByHorizon={data.reg_metrics_by_horizon || {}}
        confMatrix={data.conf_matrix || null}
        spikeEval={data.spike_eval || []}

        /* meta */
        dataWindow={data.data_window || null}
        dataQuality={data.data_quality || null}
        drift={data.drift || null}
        lineage={data.lineage || null}

        /* narrative */
        topInsights={data.top_insights || []}
        meta={data.meta || {}}
      />
    </div>
  );
};

export default ModelInsightsPage;
